// One view model for everything the UI shows about credits and the plan term.
// Built only from fields GET /billing/{tenant}/status really returns; the low-credit
// thresholds are the backend's (alert_needed ≤ 20%, topup_gate_active ≤ 5% remaining).
import { normalizePlanCode } from './billingPlans'

export const MINUTE_MS = 60 * 1000
export const HOUR_MS = 60 * MINUTE_MS
export const DAY_MS = 24 * HOUR_MS

const EXPIRING_SOON_MS = 7 * DAY_MS

// ok → nothing to say; warning → running low; low → almost out;
// exhausted → no credits left; expired → plan/trial ended (AI is paused).
export const LEVEL_TONE = Object.freeze({
  ok: 'ok',
  warning: 'warning',
  low: 'danger',
  exhausted: 'danger',
  expired: 'danger',
})

function toNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function toDate(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function clampPct(value) {
  return Math.min(100, Math.max(0, Math.round(value)))
}

function levelOf(status, total, remaining) {
  if (status.is_internal) return 'ok'
  if (status.status === 'expired') return 'expired'
  // overage_mode "allow" keeps the AI running past zero, so it is never "exhausted".
  if (total > 0 && remaining <= 0 && status.overage_mode !== 'allow') return 'exhausted'
  if (status.topup_gate_active) return 'low'
  if (status.alert_needed) return 'warning'
  return 'ok'
}

export function billingView(status, now = new Date()) {
  if (!status) return null

  const used = toNumber(status.credits_used)
  const total = toNumber(status.credits_included) + toNumber(status.credits_bonus) + toNumber(status.credits_purchased)
  const remaining = status.credits_remaining == null ? total - used : toNumber(status.credits_remaining)
  // The backend reports pct_remaining 0 when nothing was granted; that is not "100% used".
  let usedPct = 0
  if (total > 0) {
    usedPct = typeof status.pct_remaining === 'number'
      ? clampPct(100 - status.pct_remaining)
      : clampPct((used / total) * 100)
  }

  const level = levelOf(status, total, remaining)
  const expiresAt = toDate(status.subscription_expires_at ?? status.term_end)
  const msLeft = expiresAt ? expiresAt.getTime() - now.getTime() : null
  const safeMs = msLeft === null ? null : Math.max(0, msLeft)
  const timeLeft = safeMs === null ? null : {
    ms: msLeft,
    days: Math.floor(safeMs / DAY_MS),
    hours: Math.floor((safeMs % DAY_MS) / HOUR_MS),
    minutes: Math.floor((safeMs % HOUR_MS) / MINUTE_MS),
  }

  return {
    isInternal: !!status.is_internal,
    planCode: normalizePlanCode(status.tier ?? null),
    planName: status.tier_name ?? status.tier ?? null,
    subscriptionStatus: status.status ?? null,
    credits: { used, total, remaining, usedPct },
    level,
    tone: LEVEL_TONE[level],
    expiresAt,
    timeLeft,
    expiringSoon: level !== 'expired' && !status.is_internal && msLeft !== null && msLeft > 0 && msLeft < EXPIRING_SOON_MS,
    cycleEnd: toDate(status.billing_cycle_end),
  }
}

// Banners to show for a view: at most one status banner, plus the expiry countdown.
// `action` is 'renew' | 'topup' (both lead to the billing page) or null when the
// user cannot manage billing. Texts are i18n keys under billingStatus.*.
export function bannersFor(view, { canManageBilling = false } = {}) {
  if (!view || view.isInternal) return []
  const actionFor = (action) => (canManageBilling ? action : null)
  const banners = []

  if (view.level === 'expired') {
    banners.push({
      key: 'expired',
      tone: LEVEL_TONE.expired,
      textKey: view.planCode === 'trial' ? 'billingStatus.expiredTrial' : 'billingStatus.expiredPlan',
      params: {},
      action: actionFor('renew'),
    })
  } else if (view.level === 'exhausted') {
    banners.push({ key: 'exhausted', tone: LEVEL_TONE.exhausted, textKey: 'billingStatus.exhausted', params: {}, action: actionFor('topup') })
  } else if (view.level === 'low') {
    banners.push({ key: 'low', tone: LEVEL_TONE.low, textKey: 'billingStatus.low', params: { remaining: Math.max(0, view.credits.remaining) }, action: actionFor('topup') })
  } else if (view.level === 'warning') {
    banners.push({ key: 'warning', tone: LEVEL_TONE.warning, textKey: 'billingStatus.warning', params: { pct: view.credits.usedPct }, action: actionFor('topup') })
  }

  if (view.expiringSoon) {
    const { days, hours, minutes } = view.timeLeft
    banners.push({
      key: 'expiring-soon',
      tone: 'warning',
      textKey: 'billingStatus.expiringSoon',
      params: { days, hours, minutes },
      action: actionFor('renew'),
    })
  }
  return banners
}

export function formatBillingDate(date, language) {
  return date ? date.toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US') : null
}

export function hasSubscriptionChanged(status, checkout) {
  if (!status || !checkout) return false
  if (String(status.tenant_id) !== String(checkout.tenantId)) return false
  if (status.status !== 'active' || status.tier !== checkout.planCode) return false
  const baseline = checkout.baseline
  if (!baseline) return false
  // Renewing the same plan only moves term_end; the credit cycle stays put.
  return status.billing_cycle_start !== baseline.billingCycleStart
    || status.billing_cycle_end !== baseline.billingCycleEnd
    || ('termEnd' in baseline && (status.term_end ?? null) !== baseline.termEnd)
}
